// PoolScreener data layer — fetch with retry + zod validation. Network/5xx/429
// errors get exponential-backoff retries; a shape mismatch (ZodError) fails
// immediately (retrying won't change the schema).
import type { ZodType } from 'zod';

export interface FetchOpts {
  retries?: number;
  baseDelayMs?: number;
  timeoutMs?: number;
  init?: RequestInit;
}

const delay = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

async function fetchWithRetry(url: string, opts: FetchOpts): Promise<unknown> {
  const { retries = 3, baseDelayMs = 400, timeoutMs = 15_000, init } = opts;
  let lastErr: unknown;
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const res = await fetch(url, { ...init, signal: AbortSignal.timeout(timeoutMs) });
      if (!res.ok) {
        const retryable = res.status >= 500 || res.status === 429;
        if (retryable && attempt < retries) {
          await delay(baseDelayMs * 2 ** attempt);
          continue;
        }
        throw new Error(`HTTP ${res.status} for ${url}`);
      }
      return await res.json();
    } catch (err) {
      lastErr = err;
      // AbortError / network failures are retryable; a thrown HTTP error above
      // for a non-retryable status already broke out via throw.
      if (attempt < retries) {
        await delay(baseDelayMs * 2 ** attempt);
        continue;
      }
    }
  }
  throw lastErr instanceof Error ? lastErr : new Error(String(lastErr));
}

/** Fetch JSON and validate it against a zod schema. Throws ZodError on a
 *  shape mismatch (no retry) or the last network error after retries. */
export async function fetchJson<T>(
  url: string,
  schema: ZodType<T>,
  opts: FetchOpts = {},
): Promise<T> {
  const raw = await fetchWithRetry(url, opts);
  return schema.parse(raw);
}
